import React, { Fragment, useEffect, useState } from 'react'
import injectSheet from 'react-jss'
import Paper from 'pobble-components/dist/paper'
import kids from './kids.jpg'
import mis from './icon-school-mis.png'
import manual from './icon-manual-add.png'
import Button from 'pobble-components/dist/button'
import CheckIcon from 'react-icons/lib/fa/check'
import LinkIcon from 'react-icons/lib/fa/chain'
import ProfileIcon from 'react-icons/lib/fa/user'
import { Modal, ModalContent } from 'pobble-components/dist/modal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle'
import { faCircle } from '@fortawesome/pro-regular-svg-icons/faCircle'
import { faTimesCircle } from '@fortawesome/pro-solid-svg-icons/faTimesCircle'
import PobbleLink from 'pobble-components/dist/pobble-link'
import { openConfirmationDialog } from 'pobble-components/dist/modal/confirmation-dialog'
import { apiGetUserSchool } from '../../../../common/api/school'
import { apiClient } from 'pobble-components/dist/api'
import { toast } from 'pobble-components/dist/toast'

const FormControl = injectSheet({
  container: {
    position: 'relative',
    '& input': {
      appearance: 'none',
      position: 'absolute',
      backgroundColor: '#fff',
      margin: 0,
      '& + *': {
        height: '100%',
        cursor: 'pointer',
        border: '1px solid #ddd',
        padding: '16px',
        borderRadius: '4px',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        '& $checkIcon:first-child': {
          display: 'none'
        }
      },
      '&:checked + *': {
        borderColor: 'var(--blue)',
        background: '#f5fcff',
        '& $checkIcon': {
          color: 'var(--blue)',
          display: 'none',
          '&:first-child': {
            display: 'block'
          },
          '&:last-child': {
            display: 'none'
          }
        }
      },
      '&:disabled + *': {
        backgroundColor: 'var(--light-gray--)'
      }
    }
  },
  checkIcon: {
    position: 'absolute',
    right: '8px',
    top: '8px',
    color: '#ddd',
    fontSize: '16px'
  }
})(function FormControl({ classes, children, THEME, hideIcon, ...props }) {
  return (
    <label className={classes.container}>
      <input type="radio" {...props} />
      <span className={classes.component}>
        <FontAwesomeIcon className={classes.checkIcon} icon={faCheckCircle} />
        <FontAwesomeIcon className={classes.checkIcon} icon={faCircle} />
        {children}
      </span>
    </label>
  )
}, [])

const PasswordComplexityModal = injectSheet({
  content: {
    maxWidth: '720px',
    textAlign: 'center'
  }
})(({ classes, isOpen, requestClose }) => {
  return (
    <Modal isOpen={isOpen} requestClose={requestClose} closeButton>
      <ModalContent className={classes.content}>
        <PasswordComplexity requestClose={requestClose} />
      </ModalContent>
    </Modal>
  )
})

const PasswordComplexity = injectSheet({
  choice: {
    textAlign: 'left',
    color: 'var(--gray--)',
    '& svg': {
      color: 'var(--gray---)',
      margin: '1px 12px 0 0'
    },
    '& > span': {
      display: 'block',
      margin: '4px 0',
      '&:first-child': {
        fontWeight: 'bold',
        margin: '0 0 12px',
        font: 'var(--heading--)',
        color: 'var(--gray-)'
      }
    }
  },
  choicePoint: {
    display: 'flex !important'
  },
  choices: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    marginTop: '24px',
    gap: '16px',
    '@media (min-width: 768px)': {
      gridTemplateColumns: '1fr 1fr',
      '& > *': {
        height: '100%'
      }
    }
  },
  skeleton: {
    height: '125px',
    background: 'var(--light-gray--)',
    borderRadius: '4px'
  }
})(({ classes, isOpen, requestClose }) => {
  const [school, setSchool] = useState(null)

  const complexPasscodes = school?.student_strict_auth

  useEffect(() => {
    apiGetUserSchool({
      include: 'account.teacher_type.school.settings'
    }).then(res => setSchool(res.data?.account?.teacher_type?.school))
  }, [])

  const toggleComplexPasswords = value => {
    openConfirmationDialog({
      bodyText: `Are you sure you'd like to change your school's pupil passcode complexity?\n\nPlease note, your current pupil passcodes will stop working and will be replaced with new ones`,
      confirmText: `Change`,
      cancelText: `Cancel`,
      action: () => {
        requestClose()
        apiClient
          .put(`v3/schools/${school.id}`, {
            student_strict_auth: value
          })
          .then(() => toast.success('Passcode complexity has been changed'))
          .catch(() => toast.error('Something went wrong'))
      }
    })
  }

  return (
    <>
      <h3>Select pupil passcode complexity for your school</h3>
      <p>You can choose between 2 types of passcode for your pupils</p>
      <div className={classes.choices}>
        {!school && (
          <>
            <div className={classes.skeleton} />
            <div className={classes.skeleton} />
          </>
        )}
        {school && (
          <>
            <FormControl
              value={false}
              name="complexPasscodes"
              checked={!complexPasscodes}
              onChange={() => toggleComplexPasswords(false)}>
              <span className={classes.choice}>
                <span>Simple 4-digit passcode</span>
                <span className={classes.choicePoint}>
                  <FontAwesomeIcon icon={faCheckCircle} />
                  Easier to remember for pupils
                </span>
                <span className={classes.choicePoint}>
                  <FontAwesomeIcon icon={faCheckCircle} />
                  Easier sign in process
                </span>
              </span>
            </FormControl>
            <FormControl
              value={true}
              name="complexPasscodes"
              checked={complexPasscodes}
              onChange={() => toggleComplexPasswords(true)}>
              <span className={classes.choice}>
                <span>Complex 8 character passcode</span>
                <span className={classes.choicePoint}>
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <span>
                    Complies with more stringent regulations (such as
                    <PobbleLink
                      target="_blank"
                      rel="noopener noreferrer"
                      href="https://st4s.edu.au/general-information/">
                      {' '}
                      ST4S
                    </PobbleLink>
                    ) / More secure
                  </span>
                </span>
                <span className={classes.choicePoint}>
                  <FontAwesomeIcon icon={faTimesCircle} />
                  <span>Harder to remember and sign in for pupils</span>
                </span>
              </span>
            </FormControl>
          </>
        )}
      </div>
    </>
  )
})

const SchoolAdd = injectSheet(({ COLORS, BREAKPOINTS }) => ({
  container: {
    maxWidth: '1280px',
    margin: '0 auto 70px',
    [`@media (min-width: ${BREAKPOINTS.SM})`]: {
      padding: '70px 80px'
    }
  },
  wrapper: {
    [`@media (min-width: ${BREAKPOINTS.SM})`]: {
      display: 'flex'
    }
  },
  intro: {
    minWidth: '35%',
    paddingRight: '30px',
    '& svg': {
      backgroundColor: COLORS.GREEN.NORMAL,
      color: '#fff',
      borderRadius: '100%',
      padding: '2px'
    },
    '& ul': {
      listStyle: 'none',
      margin: '0',
      padding: '0',
      '& li': {
        marginBottom: '6px'
      }
    },
    '& img': {
      maxWidth: '72%',
      marginTop: '30px'
    }
  },
  island: {
    border: '1px solid #ececec',
    borderRadius: '8px',
    maxWidth: '320px',
    padding: '22px',
    margin: '14px',
    '& h4': {
      marginBottom: '4px'
    },
    '& strong': {
      color: '#a2a2a2'
    },
    '& img': {
      maxWidth: '100px'
    }
  },
  btnManual: {
    marginTop: '38px'
  },
  btnMis: {
    marginTop: '10px'
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '32px'
  }
}))(({ classes }) => {
  const [passwordModal, setPasswordModal] = useState(false)

  return (
    <Fragment>
      <Paper className={classes.container}>
        <div className={classes.wrapper}>
          <div className={classes.intro}>
            <h3>Add your pupils to Pobble if you are planning to...</h3>
            <ul>
              <li>
                <CheckIcon /> publish your children's writing.
              </li>
              <li>
                <CheckIcon /> create writing moderation files.
              </li>
              <li>
                <CheckIcon /> use our pupil logins so children can access
                Pobble.
              </li>
            </ul>
            <img src={kids} alt="Children using Pobble" />
          </div>

          <div className={classes.island}>
            <img src={mis} alt="MIS" />
            <h4>Sync your MIS</h4>
            <strong>Recommended for whole schools</strong>
            <p>
              Sync your pupils and groups to Pobble from your MIS via our
              partner, Wonde. If you make any changes to your MIS, this will
              automatically update your data – easy!
            </p>
            <Button
              className={classes.btnMis}
              tall
              primary
              href="/account/school/data"
              icon={<LinkIcon />}>
              Connect with Wonde
            </Button>
          </div>

          <div className={classes.island}>
            <img src={manual} alt="Manually" />
            <h4>Manually add pupils</h4>
            <strong>Recommended for individual classes</strong>
            <p>
              Add your pupils one-by-one or via a CSV file. Then create your own
              groups.
            </p>
            <Button
              className={classes.btnManual}
              tall
              href="/dashboard/add-pupils"
              primary
              icon={<ProfileIcon />}>
              Manually add pupils
            </Button>
          </div>
        </div>
        <footer className={classes.footer}>
          <Button tall tertiary action={() => setPasswordModal(true)}>
            Change pupil passcode complexity
          </Button>
        </footer>
      </Paper>
      <PasswordComplexityModal
        isOpen={passwordModal}
        requestClose={() => setPasswordModal(false)}
      />
    </Fragment>
  )
})

export default SchoolAdd
